// An owner's email and password (UC-25, UC-26): signing in, creating an
// account, opening the link the server mailed, and resetting a forgotten
// password. Each call turns the server's answer into
// what its screen shows, so the screens hold no error codes.

import { api } from '../api/client';
import type { Language } from '../i18n/messages';
import { lockMinutes } from './password-rules';

export { passwordLengthOk } from './password-rules';

export type SignInResult =
	| { kind: 'signedIn' }
	| { kind: 'failed' }
	| { kind: 'unverified' }
	| { kind: 'locked'; minutes: number }
	| { kind: 'unreachable' };

/** Signs in. On success the session cookie is set; the caller reloads the session. */
export async function signIn(email: string, password: string): Promise<SignInResult> {
	try {
		const { error, response } = await api.POST('/auth/password/signin', {
			body: { email: email.trim(), password },
		});
		if (response.ok) return { kind: 'signedIn' };
		switch (error?.code) {
			case 'SIGNIN_FAILED':
				return { kind: 'failed' };
			case 'EMAIL_UNVERIFIED':
				return { kind: 'unverified' };
			case 'SIGNIN_LOCKED':
				return { kind: 'locked', minutes: lockMinutes(error.details) };
			default:
				return { kind: 'unreachable' };
		}
	} catch {
		return { kind: 'unreachable' };
	}
}

export interface SignUpForm {
	name: string;
	email: string;
	password: string;
	language: Language;
}

export type SignUpResult =
	| { kind: 'sent'; email: string }
	| { kind: 'weak' }
	| { kind: 'invalid'; field: string; message: string }
	| { kind: 'unreachable' };

/**
 * Asks for the link. The answer is the same for every address, so "sent" is
 * all a screen ever learns: a new address, one that has an account and one
 * over the hour's mail look alike.
 */
export async function signUp(form: SignUpForm): Promise<SignUpResult> {
	const email = form.email.trim();
	try {
		const { error, response } = await api.POST('/auth/password/signup', {
			body: { name: form.name.trim(), email, password: form.password, language: form.language },
		});
		if (response.ok) return { kind: 'sent', email };
		if (error?.code === 'PASSWORD_WEAK') return { kind: 'weak' };
		if (error?.code === 'VALIDATION_ERROR') {
			const field = (error.details as { field?: unknown } | undefined)?.field;
			return {
				kind: 'invalid',
				field: typeof field === 'string' ? field : '',
				message: error.message ?? '',
			};
		}
		return { kind: 'unreachable' };
	} catch {
		return { kind: 'unreachable' };
	}
}

export type LinkResult = { kind: 'signedIn' } | { kind: 'invalid' } | { kind: 'unreachable' };

/** Opens a sign-up's link, or one that adds a password (UC-28), with the token its fragment carried. */
export async function verifyLink(token: string): Promise<LinkResult> {
	if (!token) return { kind: 'invalid' };
	try {
		const { error, response } = await api.POST('/auth/password/verify', { body: { token } });
		if (response.ok) return { kind: 'signedIn' };
		return error?.code === 'TOKEN_INVALID' ? { kind: 'invalid' } : { kind: 'unreachable' };
	} catch {
		return { kind: 'unreachable' };
	}
}

// The address typed on Sign in, carried to Forgot password so it need not be
// typed twice. Held here, never in the address bar, where it would be logged.
let carried = '';

export function carryEmail(email: string): void {
	carried = email.trim();
}

/** The carried address, once: a later visit starts empty. */
export function takeCarriedEmail(): string {
	const email = carried;
	carried = '';
	return email;
}

export type ForgotResult =
	{ kind: 'sent'; email: string } | { kind: 'invalid' } | { kind: 'unreachable' };

/**
 * Asks for a reset link (UC-26). The answer is the same whether or not the
 * address has an account, so "sent" says only that a link went if it could.
 */
export async function forgotPassword(email: string, language: Language): Promise<ForgotResult> {
	const address = email.trim();
	try {
		const { error, response } = await api.POST('/auth/password/forgot', {
			body: { email: address, language },
		});
		if (response.ok) return { kind: 'sent', email: address };
		return error?.code === 'VALIDATION_ERROR' ? { kind: 'invalid' } : { kind: 'unreachable' };
	} catch {
		return { kind: 'unreachable' };
	}
}

export type ResetResult =
	{ kind: 'signedIn' } | { kind: 'weak' } | { kind: 'invalid' } | { kind: 'unreachable' };

/** Sets the new password with the reset link's token; the reply signs the owner in. */
export async function resetPassword(token: string, password: string): Promise<ResetResult> {
	if (!token) return { kind: 'invalid' };
	try {
		const { error, response } = await api.POST('/auth/password/reset', {
			body: { token, password },
		});
		if (response.ok) return { kind: 'signedIn' };
		switch (error?.code) {
			case 'PASSWORD_WEAK':
				return { kind: 'weak' };
			case 'TOKEN_INVALID':
				return { kind: 'invalid' };
			default:
				return { kind: 'unreachable' };
		}
	} catch {
		return { kind: 'unreachable' };
	}
}
